import {
  Button,
  CircularProgress,
  Modal,
  Snackbar,
  SWAPSpace,
  Typography,
} from '@yosgo/swap-ui'
import React, { useEffect, useRef, useState } from 'react'
import { NextPageContext } from 'next/types'
import dayjs from 'dayjs'
import { useReactToPrint } from 'react-to-print'

import Template from '../../components/Member/ProjectComponents/Quotation/Template'
import { QuotationHead } from '../../components/Common/PagesHead'
import SignatureBlock from '../../components/Common/SignatureBlock'
import { FreshChatOpen } from '../../components/Common/FreshChat'
import { GetQuotationsFromGuess, signatureFile } from '../../utils/AppRequest'
import { signaturePixelConvertToBase64 } from '../../utils/SignaturePixelConvertToBase64'
import Axios from 'axios'
import { makeStyles, useMediaQuery } from '@material-ui/core'
import MobileCustomerFileLayout from '../../components/Member/ProjectComponents/MobileCustomerFileLayout'
import B64toBlob from '../../utils/B64toBlob'
import Skeleton from '../../components/Member/ProjectComponents/Skeleton'

const zoom = 1.5
const awsLambdaSwapPdfCreatorUrl =
  'https://v92nikoxkh.execute-api.ap-northeast-1.amazonaws.com/default/swap-pdf-creator'

export async function getServerSideProps(ctx: NextPageContext) {
  const { id, access_code } = ctx.query

  return {
    props: { id, access_code },
  }
}

const useStyles = makeStyles(() => ({
  icon: {
    width: 48,
    height: 48,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    backgroundColor: '#ffffff',
    '&:hover': {
      backgroundColor: '#ececec',
    },
  },
}))

interface QtsDataProps {
  id: string
  access_code: string
  comment: string
  effective_at: string
  created_at: string
  description: string
  amount: number
  taxAmount: number
  c_name: string
  c_phone: string
  c_email: string
  c_company_name: string
  c_company_tax_id: string
  project_file: {
    s_signature: string
    s_signatured_at: string
    c_signature: string
    c_signatured_at: string
  }
  member: {
    email: string
    verified_name: string
    verified_phone: string
    personal_id: string
  }
}

interface QuotationForCustomerProps {
  id: string
  access_code: string
}

const QuotationForCustomer = ({
  id,
  access_code,
}: QuotationForCustomerProps) => {
  const [qtsData, setQtsData] = useState<QtsDataProps>({
    id: '',
    access_code: '',
    comment: '',
    effective_at: '',
    created_at: '',
    description: '',
    amount: 0,
    taxAmount: 0,
    c_name: '',
    c_phone: '',
    c_email: '',
    c_company_name: '',
    c_company_tax_id: '',
    project_file: {
      s_signature: '',
      s_signatured_at: '',
      c_signature: '',
      c_signatured_at: '',
    },
    member: {
      email: '',
      verified_name: '',
      verified_phone: '',
      personal_id: '',
    },
  })
  const fileType = '報價單'
  const sSignatureAt = qtsData.project_file.s_signatured_at
    ? dayjs(Number(qtsData.project_file.s_signatured_at)).format(
        'YYYY-MM-DD HH:mm:ss'
      )
    : ''
  const [isOpenConfirmSignatureModal, setIsOpenConfirmSignatureModal] =
    useState(false)
  const [isOpenSignSuccessSnackbar, setIsOpenSignSuccessSnackbar] =
    useState(false)
  const [loading, setLoading] = useState(false)
  const [cSignatureForBackend, setCSignatureForBackend] = useState('')
  const [sSignature, setSSignature] = useState('')
  const [cSignatureAt, setCSignatureAt] = useState('')
  const [pdfCreatingLoading, setPdfCreatingLoading] = useState(false)
  const [clientWidth, setClientWidth] = useState(0)
  const [isOpenSignatureModal, setIsOpenSignatureModal] = useState(false)
  const [isPrinted, setIsPrinted] = useState(false)
  const [isCleanSignature, setIsCleanSignature] = useState(false)
  const [isIOS, setIsIOS] = useState(false)
  const [getDataLoading, setGetDataLoading] = useState(true)

  const isMobile: boolean = useMediaQuery('(max-width:768px)')

  useEffect(() => {
    setClientWidth(window.innerWidth)
    const userAgent = navigator.userAgent
    const matched =
      /iPhone|iPad|iPod/i.test(userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    setIsIOS(matched)
    handleGetQuotationsFromGuess()
  }, [])

  useEffect(() => {
    if (qtsData.project_file.s_signature) {
      if (
        qtsData.project_file.s_signature.indexOf('https') === -1 &&
        qtsData.project_file.s_signature.indexOf('data:image/png') === -1
      ) {
        setSSignature(
          signaturePixelConvertToBase64(qtsData.project_file.s_signature)
        )
      } else {
        setSSignature(qtsData.project_file.s_signature)
      }
    }
  }, [qtsData.project_file.s_signature])

  useEffect(() => {
    if (qtsData.project_file.c_signatured_at) {
      setCSignatureAt(
        dayjs(Number(qtsData.project_file.c_signatured_at)).format(
          'YYYY-MM-DD HH:mm:ss'
        )
      )
    }
  }, [qtsData.project_file.c_signatured_at])

  useEffect(() => {
    if (isPrinted) {
      print()
    }
  }, [isPrinted])

  const handelConfirmSignatureFile = async () => {
    setLoading(true)
    const res = await signatureFile({
      signature: cSignatureForBackend,
      file_id: id,
      target: 'CUSTOMER',
    })
    setLoading(false)
    if (res && res.errors === null) {
      setIsOpenConfirmSignatureModal(false)
      setIsOpenSignSuccessSnackbar(true)
      handleGetQuotationsFromGuess()
    } else {
      console.log('Error：', res.errors)
    }
  }

  const handleGetQuotationsFromGuess = async () => {
    const res = await GetQuotationsFromGuess({
      id: id as string,
      access_code: access_code as string,
    })
    setGetDataLoading(false)
    if (res && res.errors === null) {
      const r = res.payload

      setQtsData({
        id: r.id,
        access_code: r.access_code,
        comment: r.comment,
        effective_at: r.effective_at,
        created_at: r.created_at,
        taxAmount: r.tax_amount,
        description: r.description || r.project.description,
        amount: r.amount || r.project.amount,
        c_name: r.c_name || r.project.c_name,
        c_phone: r.c_phone || r.project.c_phone,
        c_email: r.c_email || r.project.c_email,
        c_company_name: r.c_company_name || r.project.c_company_name,
        c_company_tax_id: r.c_company_tax_id || r.project.c_company_tax_id,
        project_file: { ...r.project_file },
        member: { ...r.member },
      })

      setCSignatureAt(
        res.payload.project_file.c_signatured_at
          ? dayjs(Number(res.payload.project_file.c_signatured_at)).format(
              'YYYY-MM-DD HH:mm:ss'
            )
          : ''
      )
    } else {
      console.log('Error：', res.errors)
    }
  }

  const downloadPdf = () => {
    const data = JSON.stringify({
      mode: 'SOHOQUOTATION',
      replaceObj: {
        createAt: dayjs(Number(qtsData.created_at)).format('YYYY-MM-DD'),
        effectiveAt: dayjs(Number(qtsData.effective_at)).format('YYYY-MM-DD'),
        memberName: qtsData.member.verified_name,
        memberPhone: qtsData.member.verified_phone,
        memberEmail: qtsData.member.email,
        cName: qtsData.c_name,
        cPhone: qtsData.c_phone,
        cCompanyName: qtsData.c_company_name,
        cTaxid: qtsData.c_company_tax_id,
        cEmail: qtsData.c_email,
        caseDescription: qtsData.description,
        count: 1,
        price: qtsData.amount ? qtsData.amount.toLocaleString() : 0,
        totalAmt: qtsData.amount ? qtsData.amount.toLocaleString() : 0,
        totalAmtTax: qtsData.amount
          ? (qtsData.taxAmount - qtsData.amount).toLocaleString()
          : 0,
        totalAmtPlusTax: qtsData.taxAmount
          ? qtsData.taxAmount.toLocaleString()
          : 0,
        comment: qtsData.comment,
        sSignature: sSignature,
        seal: qtsData.project_file.s_signature
          ? 'https://yosgo-stamp.s3.ap-northeast-1.amazonaws.com/quotation.png'
          : '',
        sSignatureAt: qtsData.project_file.s_signatured_at
          ? dayjs(Number(qtsData.project_file.s_signatured_at)).format(
              'YYYY-MM-DD HH:mm:ss'
            )
          : '',
        cSignature: cSignatureForBackend
          ? cSignatureForBackend.indexOf('https') === -1 &&
            cSignatureForBackend.indexOf('data:image/png') === -1
            ? signaturePixelConvertToBase64(cSignatureForBackend)
            : cSignatureForBackend
          : qtsData.project_file.c_signature
          ? qtsData.project_file.c_signature.indexOf('https') === -1 &&
            qtsData.project_file.c_signature.indexOf('data:image/png') === -1
            ? signaturePixelConvertToBase64(qtsData.project_file.c_signature)
            : qtsData.project_file.c_signature
          : '',
        cSignatureAt: cSignatureAt,
      },
    })

    setPdfCreatingLoading(true)
    Axios.post(awsLambdaSwapPdfCreatorUrl, data)
      .then((r) => {
        setPdfCreatingLoading(false)
        download(r.data)
      })
      .catch((err) => {
        console.log(err)
        setPdfCreatingLoading(false)
      })
  }

  const download = (pdf: string) => {
    if (isIOS) {
      const blob = B64toBlob(pdf)
      const blobUrl = URL.createObjectURL(blob)
      window.open(blobUrl)
    } else {
      const link = `data:application/pdf;base64,${pdf}`
      const fileName = `${qtsData.member.verified_name}-${fileType}`
      const elem = document.createElement('a')
      elem.href = link
      elem.download = fileName
      elem.click()
    }
  }

  const printRef = useRef(null)
  const print: any = useReactToPrint({
    content: () => printRef.current,
    onAfterPrint: () => setIsPrinted(false),
  })

  useEffect(() => {
    if (printRef) {
      const elem: any = printRef.current
      window.addEventListener('resize', () => {
        const w = window.innerWidth
        elem.style.zoom = isPrinted
          ? 1.5
          : w >= 1120
          ? 1.5
          : w > 768
          ? (w - 180) / 595
          : (w - 32) / 595
      })
    }
  }, [printRef])

  const classes = useStyles()
  return (
    <>
      <QuotationHead member={qtsData.member} />
      {isMobile && clientWidth !== 0 && (
        <MobileCustomerFileLayout
          download={downloadPdf}
          print={() => setIsPrinted(true)}
          downloadLoading={pdfCreatingLoading}
        />
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          paddingTop: 72,
          paddingBottom: 80,
          backgroundColor: '#F6F6F6',
        }}
      >
        <div style={{ position: 'relative' }}>
          <div
            ref={printRef}
            style={{
              zoom: isPrinted
                ? zoom
                : isMobile
                ? (clientWidth - 32) / 595
                : clientWidth >= 1120
                ? 1.5
                : clientWidth > 768
                ? (clientWidth - 180) / 595
                : (clientWidth - 32) / 595,
            }}
          >
            {clientWidth !== 0 && (
              <Template
                isMobile={isMobile}
                personalId={qtsData.member.personal_id}
                createAt={
                  <Skeleton
                    width={100}
                    loading={getDataLoading}
                    value={dayjs(Number(qtsData.created_at)).format(
                      'YYYY-MM-DD'
                    )}
                  />
                }
                effectiveAt={
                  <Skeleton
                    width={100}
                    loading={getDataLoading}
                    value={
                      <div>
                        {dayjs(Number(qtsData.effective_at)).format(
                          'YYYY-MM-DD'
                        )}
                      </div>
                    }
                  />
                }
                memberName={
                  <Skeleton
                    width={100}
                    loading={getDataLoading}
                    value={qtsData.member.verified_name}
                  />
                }
                memberPhone={
                  <Skeleton
                    width={100}
                    loading={getDataLoading}
                    value={qtsData.member.verified_phone}
                  />
                }
                memberEmail={
                  <Skeleton
                    width={100}
                    loading={getDataLoading}
                    value={qtsData.member.email}
                  />
                }
                cName={
                  <Skeleton
                    width={100}
                    loading={getDataLoading}
                    value={qtsData.c_name}
                  />
                }
                cPhone={
                  <Skeleton
                    width={100}
                    loading={getDataLoading}
                    value={qtsData.c_phone}
                  />
                }
                cCompanyName={
                  <Skeleton
                    width={100}
                    loading={getDataLoading}
                    value={qtsData.c_company_name}
                  />
                }
                cTaxid={
                  <Skeleton
                    width={100}
                    loading={getDataLoading}
                    value={qtsData.c_company_tax_id}
                  />
                }
                cEmail={
                  <Skeleton
                    width={100}
                    loading={getDataLoading}
                    value={qtsData.c_email}
                  />
                }
                description={
                  <Skeleton
                    width={100}
                    loading={getDataLoading}
                    value={qtsData.description}
                  />
                }
                amount={
                  <Skeleton
                    width={100}
                    loading={getDataLoading}
                    value={qtsData.amount ? qtsData.amount.toLocaleString() : 0}
                  />
                }
                tax={
                  <Skeleton
                    width={100}
                    loading={getDataLoading}
                    value={
                      qtsData.amount
                        ? (qtsData.taxAmount - qtsData.amount).toLocaleString()
                        : 0
                    }
                  />
                }
                taxAmount={
                  <Skeleton
                    width={100}
                    loading={getDataLoading}
                    value={
                      qtsData.taxAmount ? qtsData.taxAmount.toLocaleString() : 0
                    }
                  />
                }
                comment={
                  <Skeleton
                    width={100}
                    loading={getDataLoading}
                    value={qtsData.comment}
                  />
                }
                commentEditButton={null}
                sSignature={
                  <div className="signature">
                    <div
                      style={{
                        position: 'absolute',
                        top: '6px',
                        textAlign: 'center',
                        width: '100%',
                      }}
                    >
                      <div
                        style={{
                          fontSize: isMobile ? 8 : 12,
                          zoom: isMobile ? 1 : 0.6666666666,
                        }}
                      >
                        平台會員簽章
                      </div>
                    </div>
                    <div
                      style={{
                        marginTop: 16,
                        height: 60,
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                    >
                      {sSignature && <img alt="sSignature" src={sSignature} height="100%" />}
                    </div>
                    <div
                      style={{
                        position: 'absolute',
                        bottom: '6.5px',
                        left: '8px',
                      }}
                    >
                      <div
                        style={{
                          fontSize: isMobile ? 8 : 12,
                          zoom: isMobile ? 1 : 0.6666666666,
                        }}
                      >
                        簽署日期：
                        <span style={{ color: '#4b4b4b' }}>{sSignatureAt}</span>
                      </div>
                    </div>
                  </div>
                }
                sSignaturedAt={sSignatureAt}
                cSignature={
                  <SignatureBlock
                    fileType={fileType}
                    who="合作客戶"
                    setIsFinished={(isFinished) => {
                      if (isFinished) {
                        setIsOpenConfirmSignatureModal(true)
                      }
                    }}
                    canSign={
                      !cSignatureForBackend &&
                      !Boolean(qtsData.project_file.c_signature) &&
                      Boolean(qtsData.project_file.s_signature) &&
                      !isMobile
                    }
                    isCleanSignature={isCleanSignature}
                    setIsCleanSignature={setIsCleanSignature}
                    signaturedAt={qtsData.project_file.c_signatured_at}
                    memberData={{
                      id: qtsData.c_company_tax_id,
                      verified_name: qtsData.c_name,
                      verified_phone: '',
                      email: '',
                      verified_state: 'CONFIRM',
                    }}
                    setSignatureForFrontend={() => {}}
                    setSignatureForBackend={(s) => {
                      setCSignatureForBackend(s)
                    }}
                    signatureForBackend={
                      cSignatureForBackend || qtsData.project_file.c_signature
                    }
                    isMobile={isMobile}
                    isMobileOpen={isOpenSignatureModal}
                    setIsMobileOpen={setIsOpenSignatureModal}
                  />
                }
                seal={
                  qtsData.project_file.s_signature && (
                    <img
                      alt="qts_seal_png"
                      src="https://swap-img.swap.work/media/yosgoseal/qts_seal.png"
                      height="100%"
                    />
                  )
                }
              />
            )}
            <div style={{ pageBreakAfter: 'always' }} />
          </div>
          {clientWidth !== 0 && (
            <>
              {isMobile &&
                !cSignatureForBackend &&
                !Boolean(qtsData.project_file.c_signature) &&
                Boolean(qtsData.project_file.s_signature) && (
                  <div
                    style={{
                      position: 'fixed',
                      bottom: 0,
                      left: 0,
                      width: '100%',
                      height: 64,
                      padding: '12px 16px',
                      backgroundColor: '#ffffff',
                      boxShadow: '0px -1px 0px #ECECEC',
                      zIndex: 1090,
                    }}
                  >
                    <Button
                      fullWidth
                      variant="primary"
                      size="small"
                      onClick={() => setIsOpenSignatureModal(true)}
                    >
                      簽名
                    </Button>
                  </div>
                )}
              <SWAPSpace size={isMobile ? 8 : 24} />
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Button
                  fullWidth={isMobile}
                  variant="secondary"
                  size={isMobile ? 'small' : 'medium'}
                  onClick={() => {
                    window.location.href = '/'
                  }}
                  style={{ marginRight: 8 }}
                >
                  前往 SWAP 首頁
                </Button>
                <Button
                  fullWidth={isMobile}
                  variant="secondary"
                  size={isMobile ? 'small' : 'medium'}
                  onClick={FreshChatOpen}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {icon_messenger}
                    <span style={{ marginLeft: 4 }}>聯繫客服</span>
                  </div>
                </Button>
              </div>
              {!isMobile && (
                <div style={{ position: 'absolute', right: -56, top: 0 }}>
                  <div
                    className={classes.icon}
                    style={{
                      marginBottom: 8,
                    }}
                    onClick={downloadPdf}
                  >
                    {pdfCreatingLoading ? <CircularProgress /> : icon_download}
                  </div>
                  <div
                    className={classes.icon}
                    onClick={() => {
                      setIsPrinted(true)
                    }}
                  >
                    {icon_print}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <Modal
        open={isOpenConfirmSignatureModal}
        onClose={() => {
          setIsOpenConfirmSignatureModal(false)
          setCSignatureForBackend('')
          setIsCleanSignature(true)
        }}
        width={480}
        title="是否確認簽名？"
        primaryButton={{
          title: '確認簽名',
          onClick: handelConfirmSignatureFile,
          loading: loading,
          disabled: loading,
        }}
        secondaryButton={{
          title: '取消',
          onClick: () => {
            setIsOpenConfirmSignatureModal(false)
            setCSignatureForBackend('')
            setIsCleanSignature(true)
          },
        }}
        children={
          <Typography variant="body2_loose" color="black800">
            確認簽名代表您({qtsData.c_company_name}
            )同意本文件。確認簽名後將無法修改或是移除簽名。
          </Typography>
        }
      />
      <Snackbar
        variant={'success'}
        checkIcon
        open={isOpenSignSuccessSnackbar}
        autoHideDuration={5000}
        onClose={() => setIsOpenSignSuccessSnackbar(false)}
        transitionDirection="up"
        transitionDuration={{ enter: 500, exit: 500 }}
        message={`已確認${fileType}簽名。`}
        closeIcon={{
          onClick: () => setIsOpenSignSuccessSnackbar(false),
        }}
      />
    </>
  )
}

const icon_messenger = (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M2.00001 12.0016C2.00051 9.80922 2.72149 7.67774 4.052 5.93522C5.3825 4.19269 7.24878 2.9357 9.36361 2.3577C11.4784 1.77969 13.7246 1.91271 15.7565 2.73628C17.7883 3.55984 19.4932 5.02831 20.6087 6.91569C21.7243 8.80307 22.1886 11.0047 21.9304 13.1819C21.6721 15.359 20.7056 17.391 19.1794 18.965C17.6533 20.539 15.6521 21.5678 13.484 21.8932C11.3159 22.2185 9.10093 21.8224 7.18001 20.7656L3.29201 21.9496C3.11859 22.0024 2.93408 22.0071 2.7582 21.9632C2.58232 21.9192 2.42169 21.8283 2.29351 21.7001C2.16532 21.5719 2.07439 21.4113 2.03044 21.2354C1.98649 21.0596 1.99118 20.875 2.04401 20.7016L3.22801 16.8076C2.41992 15.3348 1.99749 13.6815 2.00001 12.0016ZM12 4.00162C10.5799 4.0015 9.18533 4.37941 7.95957 5.09653C6.73381 5.81366 5.72103 6.84414 5.02527 8.08215C4.3295 9.32015 3.97582 10.7211 4.00055 12.141C4.02529 13.5609 4.42755 14.9486 5.16601 16.1616C5.23932 16.2821 5.2863 16.4168 5.30388 16.5567C5.32146 16.6967 5.30924 16.8387 5.26801 16.9736L4.50201 19.4916L7.01601 18.7256C7.15133 18.6844 7.29385 18.6724 7.43417 18.6903C7.57449 18.7082 7.70941 18.7557 7.83001 18.8296C8.88437 19.4733 10.0735 19.8642 11.3041 19.9714C12.5348 20.0787 13.7736 19.8995 14.9234 19.4478C16.0733 18.9962 17.1029 18.2845 17.9316 17.3683C18.7603 16.4522 19.3656 15.3566 19.7 14.1674C20.0344 12.9782 20.0889 11.7277 19.8592 10.5139C19.6295 9.30013 19.1218 8.15604 18.3759 7.1713C17.63 6.18656 16.6662 5.38793 15.56 4.83801C14.4539 4.28809 13.2353 4.00182 12 4.00162Z"
      fill="#4862CC"
    />
    <path d="M12.5508 16.5H11.0508V15H12.5508V16.5Z" fill="#4862CC" />
    <path
      d="M8.80078 10.5C8.80078 8.8425 10.1433 7.5 11.8008 7.5C13.4583 7.5 14.8008 8.8425 14.8008 10.5C14.8008 12.375 12.5508 12.5625 12.5508 14.25H11.0508C11.0508 11.8125 13.3008 12 13.3008 10.5C13.3008 9.675 12.6258 9 11.8008 9C10.9758 9 10.3008 9.675 10.3008 10.5H8.80078Z"
      fill="#4862CC"
    />
  </svg>
)

const icon_download = (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M5 20H19V18H5V20ZM19 9H15V3H9V9H5L12 16L19 9Z" fill="black" />
  </svg>
)

const icon_print = (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M19 8H5C3.34 8 2 9.34 2 11V17H6V21H18V17H22V11C22 9.34 20.66 8 19 8ZM16 19H8V14H16V19ZM19 12C18.45 12 18 11.55 18 11C18 10.45 18.45 10 19 10C19.55 10 20 10.45 20 11C20 11.55 19.55 12 19 12ZM18 3H6V7H18V3Z"
      fill="black"
    />
  </svg>
)

export default QuotationForCustomer
